"use client";

import type { CSSProperties } from "react";

import { SOCIAL_LINKS } from "@/lib/site-config";

/**
 * FloatingContact — WhatsApp + Telegram quick-contact buttons pinned to the
 * bottom-left, mirroring the Chatwoot bubble on the bottom-right. Both are
 * plain anchors (no third-party JS), so they cost nothing at load.
 *
 * Styling is deliberately theme-independent: a white circle carrying the brand
 * color in the glyph, plus a brand-tinted ring and glow so the pair reads as
 * green/blue rather than as two neutral discs. The surface tokens turned them
 * into dark chips in the dark theme, where they blended into the page — the
 * fixed white disc keeps each channel instantly recognisable on any background.
 *
 * Every brand-derived value is mixed from `--channel-brand`, so changing one
 * hex per channel restyles the glyph, ring and glow together.
 *
 * z-50 matches ScrollToTop and stays below the consent banner (z-9998), which
 * spans the full width on mobile and would otherwise be covered.
 */
const CHANNELS = [
  {
    label: "Chat on WhatsApp",
    href: SOCIAL_LINKS.whatsapp,
    brand: "#00D95A",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-5.5 w-5.5 lg:h-6 lg:w-6">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.966 1.164-.198.198-.396.223-.693.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.174-.297-.018-.458.13-.606.134-.133.347-.446.52-.669.174-.223.232-.372.347-.62.116-.249.058-.471-.03-.669-.087-.198-.783-1.887-1.075-2.586-.276-.66-.556-.571-.783-.582-.201-.01-.43-.012-.66-.012-.23 0-.602.087-.916.435-.315.347-1.2 1.17-1.2 2.855 0 1.686 1.229 3.315 1.4 3.545.173.229 2.42 3.694 5.862 5.18.82.354 1.46.565 1.958.723.752.239 1.436.205 1.977.124.605-.09 1.86-.76 2.123-1.494.263-.734.263-1.364.184-1.494-.078-.13-.287-.211-.584-.36zM12.05 21.785h-.004a9.87 9.87 0 01-5.032-1.378l-.36-.214-3.742.981.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.889-9.884a9.82 9.82 0 016.988 2.898 9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885zM20.52 3.449A11.75 11.75 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.548 4.142 1.588 5.945L0 24l6.305-1.654a11.88 11.88 0 005.74 1.464h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 00-3.423-8.468z" />
      </svg>
    ),
  },
  {
    label: "Chat on Telegram",
    href: SOCIAL_LINKS.telegram,
    brand: "#0098E5",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-5.5 w-5.5 lg:h-6 lg:w-6">
        <path d="M23.91 3.79L20.3 20.84c-.25 1.21-.98 1.5-2 .94l-5.5-4.07-2.66 2.57c-.3.3-.55.56-1.1.56-.72 0-.6-.27-.84-.95L6.3 13.7l-5.45-1.7c-1.18-.35-1.19-1.16.26-1.75l21.26-8.2c.97-.43 1.9.24 1.53 1.73z" />
      </svg>
    ),
  },
];

export default function FloatingContact() {
  return (
    <div className="fixed bottom-6 left-6 z-50 flex flex-col gap-3 lg:bottom-8 lg:left-8">
      {CHANNELS.map(({ label, href, brand, icon }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-(--channel-surface) text-(--channel-brand) ring-1 ring-(--channel-ring) shadow-(--channel-glow) transition-all duration-300 hover:-translate-y-0.5 hover:scale-105 hover:bg-(--channel-brand) hover:text-white hover:ring-(--channel-brand) hover:shadow-(--channel-glow-hover) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent lg:h-13 lg:w-13"
          style={
            {
              "--channel-brand": brand,
              // Near-white disc with just enough brand in it to read as tinted.
              "--channel-surface": `color-mix(in srgb, ${brand} 8%, #ffffff)`,
              "--channel-ring": `color-mix(in srgb, ${brand} 45%, transparent)`,
              "--channel-glow": `0 2px 6px color-mix(in srgb, ${brand} 30%, transparent), 0 8px 22px color-mix(in srgb, ${brand} 28%, transparent)`,
              "--channel-glow-hover": `0 4px 10px color-mix(in srgb, ${brand} 45%, transparent), 0 14px 30px color-mix(in srgb, ${brand} 40%, transparent)`,
            } as CSSProperties
          }
        >
          {icon}
        </a>
      ))}
    </div>
  );
}
